import React, { useState, useEffect } from 'react';
import { Box, CircularProgress, Grid, Typography } from '@mui/material';
import {
  LineChart,
  Line,
  Area,
  AreaChart,
  ComposedChart,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import {
  BOOKING_DATA_AREA_CHART_COLOR,
  COLOR_PRIMARY_2,
  COLOR_TEXT_1,
  COLOR_TEXT_2,
} from 'src/colors';
import { abbreviateNumber, formatCurrency, getMlaMonthName } from 'src/utils/functions';
import { MONTHLY_BOOKING_DATA, shortMonthNames } from 'src/const';
import { User } from 'src/interfaces/iuser';
import { useUpgradeMesageExpertFeature } from 'src/hooks/useUpgradeText';

interface Props {
  user: User;
  isDemoMarket: boolean;
  cachedMarketData: any;
  isMobileScreen: boolean;
  marketDisplayName: string;
  booking_data_key: 'booking_lead_time_distribution' | 'avg_length_of_stay';
  handleOnSampleClick: (plan: string) => void;
}

const bookingTypeFormattedNamesALOS = {
  avg_length_of_stay: 'Average Length of Stay',
};

const bookingTypeFormattedNamesBLT = {
  'booking_data_key_0-6': { label: '0-6 ', color: BOOKING_DATA_AREA_CHART_COLOR[0] },
  'booking_data_key_7-14': { label: '7-14 ', color: BOOKING_DATA_AREA_CHART_COLOR[1] },
  'booking_data_key_15-30': { label: '15-30 ', color: BOOKING_DATA_AREA_CHART_COLOR[2] },
  // '0_6': { label: '0-6 ', color: BOOKING_DATA_AREA_CHART_COLOR[0] },
  // '7_14': { label: '7-14 ', color: BOOKING_DATA_AREA_CHART_COLOR[1] },
  // '15_30': { label: '15-30 ', color: BOOKING_DATA_AREA_CHART_COLOR[2] },
};

const MonthlyBookingData: React.FC<Props> = ({
  user,
  isDemoMarket,
  cachedMarketData,
  isMobileScreen,
  marketDisplayName,
  booking_data_key,
  handleOnSampleClick,
}) => {
  const [formattedData, setFormattedData] = useState<any>([]);
  const [minMaxValue, setMinMaxValue] = useState<number[]>([Infinity, -Infinity]);

  const getFormattedChartData = () => {
    const cachedBookingData = cachedMarketData.data?.monthly_booking_data;
    if (!cachedBookingData) return [];

    let minMaxValue = [Infinity, -Infinity];

    const months = Object.keys(cachedBookingData);
    const formattedData = months.map(month => {
      const value = cachedBookingData[month][booking_data_key];

      // Max and min domain of the graph
      if (booking_data_key === 'booking_lead_time_distribution') {
        minMaxValue[0] = (Object.values(
          cachedBookingData[month][booking_data_key]
        ) as number[]).reduce((acc: number, curr: number) => Math.min(acc, curr), Infinity);
        minMaxValue[1] = (Object.values(
          cachedBookingData[month][booking_data_key]
        ) as number[]).reduce((acc: number, curr: number) => Math.max(acc, curr), -Infinity);
      } else {
        minMaxValue[0] = Math.min(minMaxValue[0], value);
        minMaxValue[1] = Math.max(minMaxValue[1], value);
      }

      if (typeof value === 'object') {
        const keys = Object.keys(value);
        const formattedObject: any = { month: month };
        keys.forEach(key => {
          formattedObject[`booking_data_key_${key}`] = value[key];
        });
        return formattedObject;
      }

      return {
        month: month,
        [booking_data_key]: value,
      };
    });
    setMinMaxValue(minMaxValue);
    return formattedData;
  };

  const monthTickFormattor = (val: string) => {
    return shortMonthNames[Number(val.split('-')[1]) - 1] + ', ' + val.split('-')[0].slice(-2);
  };

  useEffect(() => {
    const formattedChartData = getFormattedChartData();
    setFormattedData(
      formattedChartData && formattedChartData.length > 0
        ? formattedChartData
        : MONTHLY_BOOKING_DATA
    );
  }, [cachedMarketData]);

  const isLoading = cachedMarketData.loading;
  const hasRealData = cachedMarketData.data?.monthly_booking_data;
  const upgradeMessage = useUpgradeMesageExpertFeature();
  const isNotPremiumUser = !isDemoMarket && !['expert', 'scale'].includes(user.subscriptionPlan);
  const isDataBlur = !isLoading && (isNotPremiumUser || !hasRealData);

  // const initGradient = () => {
  //   return (
  //     <defs>
  //       {booking_data_key === 'booking_lead_time_distribution' &&
  //         formattedData &&
  //         formattedData.length > 0 &&
  //         Object.keys(formattedData[0])
  //           .filter(key => key !== 'month')
  //           .map((key, index) => (
  //             <linearGradient key={key} id={`gradient-${key}`} x1="0" y1="0" x2="0" y2="1">
  //               {/* fade from solid color down to transparent */}
  //               <stop
  //                 offset="5%"
  //                 stopColor={bookingTypeFormattedNamesBLT[key]?.color}
  //                 stopOpacity={0.8}
  //               />
  //               <stop
  //                 offset="95%"
  //                 stopColor={bookingTypeFormattedNamesBLT[key]?.color}
  //                 stopOpacity={0}
  //               />
  //             </linearGradient>
  //           ))}
  //     </defs>
  //   );
  // };

  return (
    <Grid
      container
      sx={{
        p: 2,
        boxShadow: '0 4px 10px #0003',
        borderRadius: 1,
        position: 'relative',
      }}
    >
      <Typography variant="body1" className="subHeadings">
        {booking_data_key === 'booking_lead_time_distribution'
          ? bookingTypeFormattedNamesBLT[booking_data_key]
          : bookingTypeFormattedNamesALOS[booking_data_key]}{' '}
        Monthly Data of {marketDisplayName}
      </Typography>

      {cachedMarketData.loading && (
        <Grid container justifyContent="center" alignItems="center" height="100%">
          <CircularProgress size={40} />
        </Grid>
      )}

      {!cachedMarketData.loading && (
        <Box
          sx={{
            mt: 2,
            position: 'relative',
            height: '300px',
            width: '100%',
          }}
        >
          <Box
            sx={{
              position: 'relative',
              height: '100%',
              width: '100%',
              filter: isDataBlur ? 'blur(4px)' : 'none',
              pointerEvents: isDataBlur ? 'none' : 'auto',
            }}
          >
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart
                data={formattedData}
                padding={0}
                margin={{ top: 20, right: 0, bottom: 20, left: 0 }}
              >
                {/* {initGradient()} */}
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis
                  dataKey="month"
                  style={{ fontFamily: 'Roboto', fontSize: '12px' }}
                  tickLine={false}
                  tickFormatter={monthTickFormattor}
                  padding={{ left: 0, right: 0 }}
                  scale="point"
                />
                <YAxis
                  axisLine={false}
                  tickLine={false}
                  domain={[minMaxValue[0] - 0.2, minMaxValue[1] + 0.1]}
                  style={{ fontFamily: 'Roboto', fontSize: '12px' }}
                  tickFormatter={val => {
                    return booking_data_key === 'booking_lead_time_distribution'
                      ? formatCurrency(val.toFixed(0))
                      : val.toFixed(1);
                  }}
                />
                {booking_data_key === 'avg_length_of_stay' && (
                  <Line
                    type="linear"
                    dataKey={booking_data_key}
                    stroke={COLOR_PRIMARY_2}
                    strokeWidth={3}
                    isAnimationActive={false}
                  />
                )}
                {/* {booking_data_key === 'booking_lead_time_distribution' &&
              formattedData &&
              formattedData.length > 0 &&
              Object.keys(formattedData[0])
                .filter(key => key !== 'month')
                .map((key, index) => {
                  return (
                    <Area
                      key={key}
                      type="linear"
                      dataKey={key}
                      stroke={bookingTypeFormattedNamesBLT[key]?.color}
                      fill={`url(#gradient-${key})`}
                      fillOpacity={0.3}
                      strokeWidth={2}
                      dot={false}
                      isAnimationActive={false}
                    />
                  );
                })} */}
                <Tooltip content={<CustomTooltip booking_data_key={booking_data_key} />} />
              </ComposedChart>
            </ResponsiveContainer>
          </Box>

          {isDataBlur && (
            <Typography
              variant="body1"
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                fontSize: isMobileScreen ? 18 : 22,
                fontWeight: 600,
                color: COLOR_PRIMARY_2,
                cursor: 'pointer',
                pointerEvents: 'auto',
                textDecoration: 'underline',
                px: 2,
                py: 1,
                borderRadius: 2,
                backgroundColor: '#fffe',
              }}
              onClick={() => isNotPremiumUser && handleOnSampleClick('expert')}
            >
              {!isLoading && isNotPremiumUser
                ? upgradeMessage
                : "We're currently processing your market data. Please refresh the page in a few minutes to view the updated insights."}
            </Typography>
          )}
        </Box>
      )}
    </Grid>
  );
};

const CustomTooltip = ({
  active,
  payload,
  label,
  booking_data_key,
}: {
  booking_data_key: 'booking_lead_time_distribution' | 'avg_length_of_stay';
  active?: boolean;
  payload?: Array<{ name: string; value: any }>;
  label?: string;
}) => {
  if (active && payload && payload.length) {
    let kpiName = '';

    const currentMonth = getMlaMonthName(payload[0]['payload'].month);
    const formattedMonth =
      shortMonthNames[Number(currentMonth.split('-')[1]) - 1] + ', ' + currentMonth.split('-')[0];

    const currentPayload = payload[0]['payload'];

    // const totalBookings =
    //   booking_data_key === 'booking_lead_time_distribution' &&
    //   Object.keys(bookingTypeFormattedNamesBLT).reduce((acc, key) => acc + currentPayload[key], 0);

    return (
      <Box
        sx={{
          maxWidth: '300px',
          backgroundColor: '#ffff',
          borderRadius: '10px',
          boxShadow: '0 4px 10px #0003',
          display: 'flex',
          flexDirection: 'column',
          padding: '10px',
          paddingRight: '30px',
        }}
      >
        <Typography variant="body1" style={{ fontWeight: 'bold', color: COLOR_TEXT_2 }}>
          {formattedMonth}
        </Typography>

        {/* {booking_data_key === 'booking_lead_time_distribution' && (
          <Box>
            {Object.keys(bookingTypeFormattedNamesBLT).map(key => (
              <Typography variant="body1" sx={{ color: COLOR_TEXT_2, mt: '10px' }}>
                <span
                  style={{
                    fontWeight: 'bold',
                    marginRight: '10px',
                    color: bookingTypeFormattedNamesBLT[key]?.color,
                  }}
                >
                  {bookingTypeFormattedNamesBLT[key]?.label}:
                </span>
                {formatCurrency(currentPayload[key] + '')}
              </Typography>
            ))}

            <Typography variant="body1" sx={{ color: COLOR_TEXT_2, mt: '10px' }}>
              <span style={{ fontWeight: 'bold', marginRight: '10px', color: COLOR_TEXT_2 }}>
                Total Bookings:
              </span>
              {totalBookings && formatCurrency(totalBookings + '')}
            </Typography>
          </Box>
        )} */}

        {booking_data_key === 'avg_length_of_stay' && (
          <Typography variant="body1" sx={{ color: COLOR_TEXT_1, mt: '10px' }}>
            <span style={{ fontWeight: 'bold', marginRight: '10px', color: payload[0]['stroke'] }}>
              {bookingTypeFormattedNamesALOS[booking_data_key]}:
            </span>
            {currentPayload[booking_data_key]} Days
          </Typography>
        )}

        <></>
      </Box>
    );
  }

  return <></>;
};

// const CustomLegend = props => {
//   return (
//     <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', gap: '20px' }}>
//       {Object.keys(bookingTypeFormattedNamesBLT).map((key, index) => (
//         <Box key={key} sx={{ display: 'flex', alignItems: 'center' }}>
//           <Box
//             sx={{
//               width: 10,
//               height: 10,
//               backgroundColor: bookingTypeFormattedNamesBLT[key]?.color,
//               borderRadius: '50%',
//               marginRight: 1,
//             }}
//           />
//           <Typography>{bookingTypeFormattedNamesBLT[key]?.label}</Typography>
//         </Box>
//       ))}
//     </Box>
//   );
// };

export default MonthlyBookingData;
